import type React from "react";
import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useGlobalState } from "senf-shared";
import { useIsMobile } from "../../../hooks/customDeviceDetect";
import { cn } from "../../../util/classNameConstructor";
import Button from "../../core/button/Button";
import Icon from "../../core/icon/Icon";

export interface CesiumDrawingFeatureProps {
  viewer: any;
  onDrawComplete?: (featureCollection: GeoJSON.FeatureCollection) => void;
  mapType?: {
    mode: string;
    drawType: string;
  };
  setDrawMode?: (mode: {
    mode: string | null;
    drawType: string | null;
  }) => void;
  initialDrawType?: string;
  handleSaveDrawn?: (featureCollection: GeoJSON.FeatureCollection) => void;
  featureCollection?: GeoJSON.FeatureCollection;
  shouldTriggerRefresh?: number;
}

type DrawingMode = "none" | "polygon" | "path" | "area" | "point";

const CesiumDrawingFeature: React.FC<CesiumDrawingFeatureProps> = ({
  initialDrawType,
  onDrawComplete,
  mapType,
  setDrawMode,
  handleSaveDrawn,
  featureCollection,
  shouldTriggerRefresh,
}) => {
  const { t } = useTranslation();
  const [positions, setPositions] = useState<any[]>([]);
  const drawingEntityRef = useRef<any>(null);
  const handlerRef = useRef<any>(null);
  const [statefulCesiumMap, setStatefulCesiumMap] =
    useGlobalState().statefulCesiumMap;
  const savedEntitiesRef = useRef<any[]>([]);
  const [internalSavedFeatures, setInternalSavedFeatures] =
    useState<GeoJSON.FeatureCollection | null>(null);
  const prevRefreshIndexRef = useRef<number | undefined>(shouldTriggerRefresh);
  const isMobile = useIsMobile();
  const [openDescription, setOpenDescription] = useState<boolean>(false);
  const [descriptionProvided, setDescriptionProvided] =
    useState<boolean>(false);
  const [featureId, setFeatureId] = useState<string | null>(null);

  useEffect(() => {
    if (!setDrawMode) return;

    if (initialDrawType === "path") {
      setDrawMode({ mode: null, drawType: null });
      // statefulMap?.df?.deleteAll();
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "path" });
      }, 10);
    }
    if (initialDrawType === "area") {
      setDrawMode({ mode: null, drawType: null });
      // statefulMap?.df?.deleteAll();
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "area" });
      }, 10);
    } else if (initialDrawType === "point") {
      setDrawMode({ mode: null, drawType: null });
      // statefulMap?.df?.deleteAll();
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "point" });
      }, 10);
    } else if (initialDrawType === "mapbounds") {
      setDrawMode({ mode: null, drawType: null });
      // statefulMap?.df?.deleteAll();
      setTimeout(() => {
        setDrawMode({ mode: "draw", drawType: "mapbounds" });
      }, 10);
    } else {
      setDrawMode({ mode: "draw", drawType: null });
      // statefulMap?.df?.deleteAll();
    }
  }, [initialDrawType, setDrawMode]);

  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) return;

    // Create a handler for mouse events
    handlerRef.current = new window.Cesium.ScreenSpaceEventHandler(
      viewer.scene.canvas,
    );

    return () => {
      if (handlerRef.current) {
        handlerRef.current.destroy();
      }
      if (drawingEntityRef.current) {
        viewer.entities.remove(drawingEntityRef.current);
      }
    };
  }, [statefulCesiumMap?.viewer]);

  const startDrawing = (mode: DrawingMode) => {
    const viewer = statefulCesiumMap?.viewer;
    console.log("startDrawing", mode, viewer, handlerRef.current);

    if (!viewer || !handlerRef.current) return;

    setPositions([]);

    // Remove any existing drawing entity
    if (drawingEntityRef.current) {
      viewer.entities.remove(drawingEntityRef.current);
    }

    // Convert area to polygon mode
    const effectiveMode = mode === "area" ? "polygon" : mode;

    if (effectiveMode === "polygon") {
      // Create a new entity for the polygon
      drawingEntityRef.current = viewer.entities.add({
        polygon: {
          hierarchy: new window.Cesium.PolygonHierarchy(),
          material: window.Cesium.Color.WHITE.withAlpha(0.3),
          outline: true,
          outlineColor: window.Cesium.Color.WHITE,
        },
      });
    } else if (effectiveMode === "path") {
      // Create a new entity for the line
      drawingEntityRef.current = viewer.entities.add({
        polyline: {
          positions: [],
          width: 4,
          material: new window.Cesium.PolylineGlowMaterialProperty({
            glowPower: 0.15,
            color: window.Cesium.Color.CYAN,
          }),
          clampToGround: true,
        },
      });
    } else if (effectiveMode === "point") {
      // Create a new entity for the point
      drawingEntityRef.current = viewer.entities.add({
        point: {
          pixelSize: 10,
          color: window.Cesium.Color.RED,
          outlineColor: window.Cesium.Color.WHITE,
          outlineWidth: 2,
          heightReference: window.Cesium.HeightReference.RELATIVE_TO_GROUND,
          height: 1,
          disableDepthTestDistance: Number.POSITIVE_INFINITY,
        },
      });
    }

    // Handle left click to add points
    handlerRef.current.setInputAction((movement: any) => {
      const cartesian = viewer.scene.pickPosition(movement.position);
      if (cartesian) {
        console.log("Point clicked", { effectiveMode, cartesian });
        setPositions((prev) => {
          const newPositions = [...prev, cartesian];
          console.log("Updated positions", {
            prevLength: prev.length,
            newLength: newPositions.length,
          });

          // Update the entity based on drawing mode
          if (effectiveMode === "polygon") {
            drawingEntityRef.current.polygon.hierarchy =
              new window.Cesium.PolygonHierarchy(newPositions);
          } else if (effectiveMode === "path") {
            drawingEntityRef.current.polyline.positions = newPositions;
          } else if (effectiveMode === "point") {
            drawingEntityRef.current.position = cartesian;
            console.log("Point entity updated", { position: cartesian });
            // For points, we want to complete the drawing immediately
            // Use setTimeout to ensure the state update is applied before calling handleSave
            setTimeout(() => {
              // Make sure we have at least one point before saving
              if (newPositions.length > 0) {
                console.log("Calling handleSave for point", {
                  positionsLength: newPositions.length,
                });
                handleSave();
              }
            }, 0);
          }

          return newPositions;
        });
      }
    }, window.Cesium.ScreenSpaceEventType.LEFT_CLICK);

    // Add right-click handler to complete drawing
    handlerRef.current.setInputAction(() => {
      if (effectiveMode !== "point") {
        handleSave();
      }
    }, window.Cesium.ScreenSpaceEventType.RIGHT_CLICK);
  };

  console.log("mapType", mapType);
  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    console.log("useEffect", mapType?.drawType, viewer, handlerRef.current);
    if (mapType?.drawType && viewer && handlerRef.current) {
      startDrawing(mapType?.drawType as DrawingMode);
    }
  }, [mapType?.drawType, statefulCesiumMap?.viewer]);

  const handleSave = () => {
    const viewer = statefulCesiumMap?.viewer;
    const drawType = mapType?.drawType;
    const isPolygon = drawType === "polygon" || drawType === "area";
    const isPoint = drawType === "point";

    // Get positions from the entity instead of the state
    let currentPositions: any[] = [];

    if (drawingEntityRef.current) {
      if (isPolygon && drawingEntityRef.current.polygon) {
        currentPositions =
          drawingEntityRef.current.polygon.hierarchy.getValue().positions;
      } else if (drawingEntityRef.current.polyline) {
        currentPositions =
          drawingEntityRef.current.polyline.positions.getValue();
      } else if (isPoint && drawingEntityRef.current.position) {
        currentPositions = [drawingEntityRef.current.position.getValue()];
      }
    }

    const hasEnoughPoints = isPoint
      ? currentPositions.length >= 1
      : isPolygon
        ? currentPositions.length >= 3
        : currentPositions.length >= 2;

    console.log("handleSave", {
      drawType,
      isPoint,
      positionsLength: currentPositions.length,
      hasEnoughPoints,
    });

    if (!hasEnoughPoints) {
      alert(
        t("Need more points: ") +
          (isPolygon
            ? "3 for polygon"
            : isPoint
              ? "1 for point"
              : "2 for line"),
      );
      return;
    }

    // For points, we need to make sure we have exactly one point
    if (isPoint && currentPositions.length > 1) {
      // If we have more than one point, just use the first one
      currentPositions = [currentPositions[0]];
    }

    const coordinates = currentPositions.map((position) => {
      const cartographic = window.Cesium.Cartographic.fromCartesian(position);
      return [
        window.Cesium.Math.toDegrees(cartographic.longitude),
        window.Cesium.Math.toDegrees(cartographic.latitude),
      ];
    });

    // For polygons, close the shape by adding the first point at the end
    if (isPolygon && coordinates.length > 0) {
      coordinates.push(coordinates[0]);
    }

    // Create a GeoJSON feature
    const feature: GeoJSON.Feature = {
      type: "Feature",
      properties: {
        // Add default properties that might be needed by handleSaveDrawn
        number: 1,
        description: "",
        choices: null,
      },
      geometry: {
        type: isPolygon ? "Polygon" : isPoint ? "Point" : "LineString",
        coordinates: isPolygon
          ? [coordinates]
          : isPoint
            ? coordinates[0]
            : coordinates,
      },
    };

    // Create a FeatureCollection with just this feature
    const newFeatureCollection: GeoJSON.FeatureCollection = {
      type: "FeatureCollection",
      features: [feature],
    };

    console.log("newFeatureCollection", newFeatureCollection);

    // Update internal saved features
    setInternalSavedFeatures((prevFeatures) => {
      if (!prevFeatures) return newFeatureCollection;

      const updatedFeatures: GeoJSON.FeatureCollection = {
        type: "FeatureCollection",
        features: [...prevFeatures.features, feature],
      };

      // Call handleSaveDrawn with the complete feature collection
      if (handleSaveDrawn) {
        handleSaveDrawn(updatedFeatures);
      } else if (onDrawComplete) {
        onDrawComplete(updatedFeatures);
      }

      return updatedFeatures;
    });

    // If there are no previous features, call the callback with just the new feature
    if (!internalSavedFeatures && (handleSaveDrawn || onDrawComplete)) {
      if (handleSaveDrawn) {
        handleSaveDrawn(newFeatureCollection);
      } else if (onDrawComplete) {
        onDrawComplete(newFeatureCollection);
      }
    }

    // Clean up after saving
    setPositions([]);
    if (drawingEntityRef.current) {
      viewer?.entities.remove(drawingEntityRef.current);
    }
    if (handlerRef.current) {
      handlerRef.current.removeInputAction(
        window.Cesium.ScreenSpaceEventType.LEFT_CLICK,
      );
      handlerRef.current.removeInputAction(
        window.Cesium.ScreenSpaceEventType.RIGHT_CLICK,
      );
    }

    // For point mode, we need to restart drawing immediately
    if (isPoint && mapType?.drawType) {
      // Start drawing again with the same type
      setTimeout(() => {
        startDrawing(mapType.drawType as DrawingMode);
      }, 100);
    }
  };

  const cancelDrawing = () => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) return;

    setPositions([]);
    if (drawingEntityRef.current) {
      viewer.entities.remove(drawingEntityRef.current);
    }
    if (handlerRef.current) {
      handlerRef.current.removeInputAction(
        window.Cesium.ScreenSpaceEventType.LEFT_CLICK,
      );
      handlerRef.current.removeInputAction(
        window.Cesium.ScreenSpaceEventType.RIGHT_CLICK,
      );
    }

    // Reset drawing mode
    setDrawMode?.({ mode: "none", drawType: null });
  };

  const handleSetAddDescription = (id: string, action: string) => {
    setFeatureId(id);
    setOpenDescription(true);
    // Here you would typically show a description input dialog
    // For now, we'll just simulate it by setting descriptionProvided to true
    setDescriptionProvided(true);
  };

  const handleSaveAll = (id: string) => {
    handleSave();
    setOpenDescription(false);
    setDescriptionProvided(false);
    setFeatureId(null);

    // Reset drawing mode but keep the drawType so user can draw again
    setDrawMode?.({ mode: "none", drawType: null });

    // Add a small delay before allowing drawing again to ensure cleanup is complete
    setTimeout(() => {
      // Start drawing again with the same type
      if (mapType?.drawType) {
        // Explicitly set the drawing mode back to "draw" with the same drawType
        setDrawMode?.({ mode: "draw", drawType: mapType.drawType });
        startDrawing(mapType.drawType as DrawingMode);
      }
    }, 100);
  };

  const handleDelete = (id: string) => {
    cancelDrawing();
    setFeatureId(null);
  };

  // Check if we're in drawing mode
  const isDrawingMode = mapType?.mode === "draw";
  const isPolygon = mapType?.drawType === "polygon";
  const isPath = mapType?.drawType === "path";
  const isPoint = mapType?.drawType === "point";

  // Add a new useEffect to display saved features
  useEffect(() => {
    const viewer = statefulCesiumMap?.viewer;
    if (!viewer) {
      console.log("No viewer available for displaying features");
      return;
    }

    // Use either the prop or internal state
    const featuresToDisplay = featureCollection || internalSavedFeatures;
    if (!featuresToDisplay) {
      console.log("No features to display");
      return;
    }

    if (
      !featuresToDisplay.features ||
      featuresToDisplay.features.length === 0
    ) {
      console.log("Feature collection is empty");
      return;
    }

    console.log(
      "Displaying saved features:",
      featuresToDisplay.features.length,
    );

    // Clear any previously displayed saved features
    savedEntitiesRef.current.forEach((entity) => {
      viewer.entities.remove(entity);
    });
    savedEntitiesRef.current = [];

    // Display each saved feature
    featuresToDisplay.features.forEach((feature, index) => {
      if (!feature.geometry) {
        console.log(`Feature ${index} has no geometry`);
        return;
      }

      const geometryType = feature.geometry.type;
      console.log(
        `Processing feature ${index} with geometry type: ${geometryType}`,
      );

      // Convert GeoJSON coordinates to Cesium positions
      let positions: any[] = [];

      try {
        if (geometryType === "Point" && "coordinates" in feature.geometry) {
          const coordinates = feature.geometry.coordinates;
          positions = [
            window.Cesium.Cartesian3.fromDegrees(
              coordinates[0],
              coordinates[1],
            ),
          ];
          console.log(
            `Point coordinates: ${coordinates[0]}, ${coordinates[1]}`,
          );
        } else if (
          geometryType === "LineString" &&
          "coordinates" in feature.geometry
        ) {
          const coordinates = feature.geometry.coordinates;
          positions = coordinates.map((coord: number[]) =>
            window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1]),
          );
          console.log(`LineString with ${coordinates.length} points`);
        } else if (
          geometryType === "Polygon" &&
          "coordinates" in feature.geometry
        ) {
          const coordinates = feature.geometry.coordinates;
          positions = coordinates[0].map((coord: number[]) =>
            window.Cesium.Cartesian3.fromDegrees(coord[0], coord[1]),
          );
          console.log(`Polygon with ${coordinates[0].length} points`);
        } else {
          console.log(
            `Unsupported geometry type or missing coordinates: ${geometryType}`,
          );
          return;
        }
      } catch (error) {
        console.error(
          `Error processing coordinates for feature ${index}:`,
          error,
        );
        return;
      }

      // Create the appropriate entity based on geometry type
      let entity;
      try {
        if (geometryType === "Point") {
          // Use color from properties if available, otherwise use default
          const color = feature.properties?.color
            ? window.Cesium.Color.fromCssColorString(feature.properties.color)
            : window.Cesium.Color.RED;

          entity = viewer.entities.add({
            position: positions[0],
            point: {
              pixelSize: 10,
              color: color,
              outlineColor: window.Cesium.Color.WHITE,
              outlineWidth: 2,
              heightReference: window.Cesium.HeightReference.CLAMP_TO_GROUND,
            },
          });
          console.log(
            `Added point entity at ${positions[0].x}, ${positions[0].y}, ${positions[0].z}`,
          );
        } else if (geometryType === "LineString") {
          // Use color from properties if available, otherwise use default
          const color = feature.properties?.color
            ? window.Cesium.Color.fromCssColorString(feature.properties.color)
            : window.Cesium.Color.CYAN;

          entity = viewer.entities.add({
            polyline: {
              positions: positions,
              width: 10,
              material: new window.Cesium.PolylineGlowMaterialProperty({
                glowPower: 0.15,
                color: color,
              }),
              clampToGround: true,
            },
          });
          console.log(`Added line entity with ${positions.length} points`);
        } else if (geometryType === "Polygon") {
          // Get base color from properties if available, otherwise use default
          const baseColor = feature.properties?.color
            ? window.Cesium.Color.fromCssColorString(feature.properties.color)
            : window.Cesium.Color.WHITE;

          // Get opacity from properties if available, otherwise use default
          let opacity = 0.4; // Default opacity
          if (feature.properties?.opacity !== undefined) {
            const parsedOpacity = Number.parseFloat(feature.properties.opacity);
            if (
              !isNaN(parsedOpacity) &&
              parsedOpacity >= 0 &&
              parsedOpacity <= 1
            ) {
              opacity = parsedOpacity;
            }
          }

          // Create a color with the specified opacity
          const fillColor = window.Cesium.Color.fromAlpha(baseColor, opacity);
          console.log(
            `Polygon color: ${fillColor.toString()}, opacity: ${opacity}`,
          );

          const outlineColor = feature.properties?.outlineColor
            ? window.Cesium.Color.fromCssColorString(
                feature.properties.outlineColor,
              )
            : window.Cesium.Color.WHITE;

          entity = viewer.entities.add({
            polygon: {
              hierarchy: new window.Cesium.PolygonHierarchy(positions),
              material: new window.Cesium.ColorMaterialProperty(fillColor),
              outline: true,
              outlineColor: outlineColor,
            },
          });
          console.log(
            `Added polygon entity with ${positions.length} points and opacity ${opacity}`,
          );
        }
      } catch (error) {
        console.error(`Error creating entity for feature ${index}:`, error);
        return;
      }

      if (entity) {
        savedEntitiesRef.current.push(entity);
        console.log(`Successfully added entity for feature ${index}`);
      }
    });

    console.log(
      `Displayed ${savedEntitiesRef.current.length} entities on the map`,
    );
  }, [featureCollection, internalSavedFeatures, statefulCesiumMap?.viewer]);

  // Add an effect to initialize internalSavedFeatures from featureCollection when component mounts
  useEffect(() => {
    if (
      featureCollection &&
      (!internalSavedFeatures || internalSavedFeatures.features.length === 0)
    ) {
      console.log(
        "Initializing internal saved features from props:",
        featureCollection.features.length,
      );
      setInternalSavedFeatures(featureCollection);
    }
  }, [featureCollection, internalSavedFeatures]);

  // Replace the shouldTriggerRefresh useEffect with this one
  useEffect(() => {
    // Only trigger when shouldTriggerRefresh changes
    if (
      shouldTriggerRefresh !== undefined &&
      shouldTriggerRefresh !== prevRefreshIndexRef.current
    ) {
      console.log(
        "Refreshing drawn features due to index change:",
        shouldTriggerRefresh,
      );

      // Clear internal saved features
      setInternalSavedFeatures(null);

      // Clear all entities from the map
      const viewer = statefulCesiumMap?.viewer;
      if (viewer) {
        savedEntitiesRef.current.forEach((entity) => {
          viewer.entities.remove(entity);
        });
        savedEntitiesRef.current = [];
      }

      // Reset drawing mode
      setDrawMode?.({ mode: "none", drawType: null });

      // Reset positions
      setPositions([]);

      // Clean up any active drawing
      if (drawingEntityRef.current && viewer) {
        viewer.entities.remove(drawingEntityRef.current);
        drawingEntityRef.current = null;
      }

      if (handlerRef.current) {
        handlerRef.current.removeInputAction(
          window.Cesium.ScreenSpaceEventType.LEFT_CLICK,
        );
        handlerRef.current.removeInputAction(
          window.Cesium.ScreenSpaceEventType.RIGHT_CLICK,
        );
      }
    }

    // Update the ref for the next comparison
    prevRefreshIndexRef.current = shouldTriggerRefresh;
  }, [shouldTriggerRefresh, statefulCesiumMap?.viewer, setDrawMode]);

  return (
    <div className="z-[9999999999999] flex flex-col gap-2">
      {isDrawingMode && positions.length > 0 && (
        <div
          className={cn(
            "flex justify-center z-[1]",
            isMobile
              ? "fixed left-0 w-screen"
              : "absolute left-1/2 -translate-x-1/2 w-max",
          )}
          style={{ bottom: "24px" }}
        >
          <div
            className={cn(
              "w-full h-auto flex flex-col pointer-events-all md:rounded-3xl gap-2.5 p-5 bg-surface-default shadow-lg border border-border-default z-[999999999] transition-colors duration-200",
            )}
          >
            <div
              className={cn(
                "flex gap-2",
                isMobile ? "ml-[50px] w-[calc(100%-50px)]" : "w-full",
              )}
            >
              <div className="w-full flex justify-end">
                <Button
                  size="lg"
                  text={t("util:delete", {
                    val: t(
                      `global:${
                        mapType?.drawType === "point"
                          ? "point"
                          : mapType?.drawType === "path"
                            ? "path"
                            : "area"
                      }`,
                    ),
                  })}
                  width="max"
                  onClick={() => {
                    handleDelete(featureId || "");
                  }}
                />
              </div>
              <Button
                variant="primary"
                leadingIcon="checkmark-md"
                text={isMobile ? "" : t("global:save")}
                size="lg"
                disabled={openDescription === true && !descriptionProvided}
                disabledNotice={t("map:drawInfoTooltip.descriptionMissing")}
                dataCy="drawingFeatureSave"
                onClick={() => {
                  handleSetAddDescription(featureId || "new-feature", "save");
                  handleSaveAll(featureId || "new-feature");
                }}
              />
            </div>
            <div className="text-sm text-gray-500 mt-1">
              <span>
                {isPolygon
                  ? `${positions.length}/3 points needed`
                  : isPoint
                    ? `${positions.length}/1 point needed`
                    : `${positions.length}/2 points needed`}
              </span>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CesiumDrawingFeature;
